"use client";

import { bindDevToolboxScenarioIdListener } from "@/lib/devToolbox/bindScenarioListener";
import { isDevToolboxEnabled } from "@/lib/devToolbox/isDevToolboxEnabled";
import { getActiveDevToolboxScenarioId } from "@/lib/devToolbox/scenarioRuntime";
import { ONBOARDING_STORAGE_KEYS, writeOnboardingFlag } from "@/lib/onboarding";
import type { OnboardingOpenSource } from "@/lib/onboarding";
import type { OnboardingPromptId } from "@/lib/onboardingEngine/types";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";
import {
  enableNewUserWalkthrough,
  resetNewUserWalkthroughClientState,
} from "@/lib/devToolbox/newUserWalkthrough";

export const ONBOARDING_DEV_PREVIEW_QUERY = "devPreview";
export const ONBOARDING_DEV_PREVIEW_VALUE = "onboarding";

export type OnboardingDevToolboxContext = {
  openTour: (options?: {
    audience?: "guest" | "auth";
    source?: OnboardingOpenSource;
  }) => void;
  openPrompt: (promptId: OnboardingPromptId) => void;
};

export function isOnboardingRegistrationPreviewScenario(
  scenarioId: DevToolboxScenarioId | null | undefined,
): boolean {
  return scenarioId?.startsWith("onboarding.registration.") ?? false;
}

/** Dev-only: skip auth/onboarding funnel redirects for registration UI previews. */
export function shouldBypassOnboardingRegistrationPreview(
  searchParams?: Pick<URLSearchParams, "get"> | null,
): boolean {
  if (!isDevToolboxEnabled()) return false;

  const previewFromParams =
    searchParams?.get(ONBOARDING_DEV_PREVIEW_QUERY) ??
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get(
          ONBOARDING_DEV_PREVIEW_QUERY,
        )
      : null);

  if (previewFromParams === ONBOARDING_DEV_PREVIEW_VALUE) {
    return true;
  }

  return isOnboardingRegistrationPreviewScenario(
    getActiveDevToolboxScenarioId(),
  );
}

function clearPromptFlag(promptId: OnboardingPromptId): void {
  if (promptId === "homepageTour") {
    writeOnboardingFlag(ONBOARDING_STORAGE_KEYS.homepageAutoshowDone, false);
    writeOnboardingFlag(
      ONBOARDING_STORAGE_KEYS.guestHomepageAutoshowDone,
      false,
    );
    return;
  }
  if (promptId === "sellIntro") {
    writeOnboardingFlag(ONBOARDING_STORAGE_KEYS.sellPreformDone, false);
    return;
  }
  if (promptId === "marketIntro") {
    writeOnboardingFlag(ONBOARDING_STORAGE_KEYS.marketIntroDone, false);
    return;
  }
  if (promptId === "myItemsStatusIntro") {
    writeOnboardingFlag(ONBOARDING_STORAGE_KEYS.myItemsStatusModalDone, false);
  }
}

export function applyOnboardingDevToolboxScenario(
  scenarioId: DevToolboxScenarioId | null,
  context: OnboardingDevToolboxContext,
): (() => void) | void {
  if (!scenarioId?.startsWith("onboarding.")) return;

  const timer = window.setTimeout(() => {
    switch (scenarioId) {
      case "onboarding.flow.new-user-journey":
        resetNewUserWalkthroughClientState();
        enableNewUserWalkthrough();
        writeOnboardingFlag(
          ONBOARDING_STORAGE_KEYS.guestHomepageAutoshowDone,
          false,
        );
        context.openTour({ audience: "guest", source: "floating_help" });
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("dev-toolbox-walkthrough-change"));
        }
        break;
      case "onboarding.tour.homepage-guest":
        writeOnboardingFlag(
          ONBOARDING_STORAGE_KEYS.guestHomepageAutoshowDone,
          false,
        );
        context.openTour({ audience: "guest", source: "floating_help" });
        break;
      case "onboarding.tour.homepage-auth":
        writeOnboardingFlag(
          ONBOARDING_STORAGE_KEYS.homepageAutoshowDone,
          false,
        );
        context.openTour({ audience: "auth", source: "floating_help" });
        break;
      case "onboarding.prompt.market-intro":
        clearPromptFlag("marketIntro");
        context.openPrompt("marketIntro");
        break;
      case "onboarding.prompt.sell-intro":
        clearPromptFlag("sellIntro");
        context.openPrompt("sellIntro");
        break;
      case "onboarding.prompt.my-items-status":
        clearPromptFlag("myItemsStatusIntro");
        context.openPrompt("myItemsStatusIntro");
        break;
      default:
        break;
    }
  }, 150);

  return () => window.clearTimeout(timer);
}

export function subscribeOnboardingDevToolboxAdapter(
  context: OnboardingDevToolboxContext,
): () => void {
  if (!isDevToolboxEnabled()) return () => {};

  let cleanup: (() => void) | void;

  const unsubscribe = bindDevToolboxScenarioIdListener((scenarioId) => {
    cleanup?.();
    cleanup = applyOnboardingDevToolboxScenario(scenarioId, context);
  });

  return () => {
    cleanup?.();
    unsubscribe();
  };
}
